/**
 * LEOS Mission Ready - Netlify Identity Authentication Service
 * Integrates with Netlify Identity for real authentication
 */

class NetlifyAuthService {
    constructor() {
        this.user = null;
        this.token = null;
        this.initialized = false;
        this.callbacks = {
            onLogin: [],
            onLogout: [],
            onSignup: [],
            onError: []
        };
        
        // Initialize Netlify Identity
        this.init();
    }
    
    init() {
        if (typeof window.netlifyIdentity === 'undefined') {
            console.warn('Netlify Identity widget not loaded. Using demo mode.');
            this.useDemoMode();
            return;
        }
        
        // Initialize the widget
        window.netlifyIdentity.init();
        
        // Set up event handlers
        window.netlifyIdentity.on('init', user => {
            this.initialized = true;
            if (user) {
                this.handleUser(user);
            }
        });
        
        window.netlifyIdentity.on('login', user => {
            this.handleUser(user);
            this.triggerCallbacks('onLogin', user);
        });
        
        window.netlifyIdentity.on('logout', () => {
            this.user = null;
            this.token = null;
            this.triggerCallbacks('onLogout');
        });
        
        window.netlifyIdentity.on('signup', user => {
            this.handleUser(user);
            this.triggerCallbacks('onSignup', user);
        });
        
        window.netlifyIdentity.on('error', err => {
            console.error('Netlify Identity error:', err);
            this.triggerCallbacks('onError', err);
        });
    }
    
    async handleUser(user) {
        this.user = user;
        
        // Get fresh JWT token
        try {
            this.token = await user.jwt();
            
            // Refresh token before it expires
            const tokenData = this.parseJWT(this.token);
            const expiresIn = (tokenData.exp * 1000) - Date.now() - 60000; // Refresh 1 min before expiry
            
            setTimeout(() => {
                this.refreshToken();
            }, Math.max(expiresIn, 0));
            
        } catch (error) {
            console.error('Error getting JWT token:', error);
        }
    }
    
    async refreshToken() {
        if (this.user) {
            try {
                this.token = await this.user.jwt(true); // Force refresh
                
                // Schedule next refresh
                const tokenData = this.parseJWT(this.token);
                const expiresIn = (tokenData.exp * 1000) - Date.now() - 60000;
                
                setTimeout(() => {
                    this.refreshToken();
                }, Math.max(expiresIn, 0));
                
            } catch (error) {
                console.error('Error refreshing token:', error);
            }
        }
    }
    
    parseJWT(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error parsing JWT:', error);
            return {};
        }
    }
    
    // Auth methods
    login() {
        if (window.netlifyIdentity) {
            window.netlifyIdentity.open('login');
        } else {
            this.useDemoMode();
        }
    }
    
    signup() {
        if (window.netlifyIdentity) {
            window.netlifyIdentity.open('signup');
        } else {
            this.useDemoMode();
        }
    }
    
    logout() {
        if (window.netlifyIdentity && this.user) {
            window.netlifyIdentity.logout();
        } else {
            // Demo logout
            this.user = null;
            this.token = null;
            this.triggerCallbacks('onLogout');
        }
    }
    
    // Get current user info
    getCurrentUser() {
        return this.user;
    }
    
    getToken() {
        return this.token;
    }
    
    getUserInfo() {
        if (!this.user) return null;
        
        return {
            id: this.user.id,
            email: this.user.email,
            name: this.user.user_metadata?.full_name || this.user.email.split('@')[0],
            avatar: this.user.user_metadata?.avatar_url || '/assets/kelly.svg',
            role: this.user.app_metadata?.role || 'User',
            tier: this.user.app_metadata?.tier || 'mission-ready',
            company: this.user.user_metadata?.company || '',
            created: this.user.created_at
        };
    }
    
    isAuthenticated() {
        return !!this.user;
    }
    
    // API calls with authentication
    async callAPI(endpoint, options = {}) {
        if (!this.token) {
            throw new Error('Not authenticated');
        }
        
        // Determine base URL based on environment
        const baseURL = window.location.hostname === 'localhost' 
            ? 'http://localhost:8888/.netlify/functions'
            : '/.netlify/functions';
        
        const response = await fetch(`${baseURL}${endpoint}`, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Request failed' }));
            throw new Error(error.error || `API error: ${response.status}`);
        }
        
        return response.json();
    }
    
    // Event handling
    on(event, callback) {
        if (this.callbacks[event]) {
            this.callbacks[event].push(callback);
        }
    }
    
    off(event, callback) {
        if (this.callbacks[event]) {
            this.callbacks[event] = this.callbacks[event].filter(cb => cb !== callback);
        }
    }
    
    triggerCallbacks(event, data) {
        if (this.callbacks[event]) {
            this.callbacks[event].forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Error in ${event} callback:`, error);
                }
            });
        }
    }
    
    // Demo mode fallback
    useDemoMode() {
        console.log('Using demo authentication mode');
        
        // Simulate login with demo user
        setTimeout(() => {
            this.user = {
                id: 'demo-user',
                email: 'kelly.johnson@skunkworks.com',
                user_metadata: {
                    full_name: 'Kelly Johnson',
                    avatar_url: '/assets/kelly.svg',
                    company: 'Skunk Works'
                },
                app_metadata: {
                    role: 'Flight Director',
                    tier: 'mission-ready'
                },
                created_at: new Date().toISOString()
            };
            
            // Create a demo token
            this.token = 'demo-token-' + Date.now();
            
            this.triggerCallbacks('onLogin', this.user);
        }, 1000);
    }
}

// Create singleton instance
const netlifyAuth = new NetlifyAuthService();

// Export for use in other modules
export { netlifyAuth };